/**
 * OPS-DASHBOARD-RBAC-HARDENING-001 — tenant-safe permission key matching (Java parity).
 */

import { extractAppsPrefix } from './governance/tenantGovernanceIdentity.js';

/** Tenant / apps-code prefixes only — not permission-family tokens like DOCUMENTAI. */
const TENANT_APPS_PREFIX_RE = /^(FONE|CHERITTO|REG[0-9A-Z]+)$/i;

/**
 * Bare action/level tokens must not match via endsWith (DOCUMENTAI:API → API must not
 * unlock every *:API grant such as OPDCONSULTATION:API).
 */
const GENERIC_ACTION_TOKENS = new Set([
  'API',
  'VIEW',
  'EDIT',
  'READ',
  'NEW',
  'LIST',
  'CREATE',
  'UPDATE',
  'UPDATESTATUS',
  'CONFIGURE',
  'APPROVE',
  'ADMIN_OVERRIDE',
]);

export function normalizePermissionKey(permission) {
  return String(permission ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

function isTenantAppsPrefix(prefix) {
  return TENANT_APPS_PREFIX_RE.test(String(prefix ?? '').trim());
}

function tenantPrefix(identifier) {
  const prefix = extractAppsPrefix(identifier);
  return isTenantAppsPrefix(prefix) ? prefix : '';
}

/** Permission token after the tenant/apps prefix (first colon), normalized. */
export function permissionFamilySuffix(permission) {
  if (!permission?.trim()) {
    return '';
  }
  const prefix = tenantPrefix(permission);
  if (!prefix) {
    return normalizePermissionKey(permission);
  }
  const colon = permission.indexOf(':');
  return normalizePermissionKey(permission.slice(colon + 1));
}

/** Match workflow/page keys within the same tenant prefix (suffix-only legacy tokens allowed). */
export function permissionKeysMatch(wanted, granted) {
  if (!wanted?.trim() || !granted?.trim()) {
    return false;
  }
  const wantedPrefix = tenantPrefix(wanted);
  const grantedPrefix = tenantPrefix(granted);
  const w = normalizePermissionKey(wanted);
  const g = normalizePermissionKey(granted);
  if (g === w) {
    return true;
  }
  if (!GENERIC_ACTION_TOKENS.has(w) && g.endsWith(`:${w}`)) {
    return true;
  }
  if (!GENERIC_ACTION_TOKENS.has(g) && w.endsWith(`:${g}`)) {
    return true;
  }
  if (wantedPrefix && grantedPrefix) {
    if (wantedPrefix.toUpperCase() !== grantedPrefix.toUpperCase()) {
      return false;
    }
    const wantedFamily = permissionFamilySuffix(wanted);
    const grantedFamily = permissionFamilySuffix(granted);
    return Boolean(wantedFamily) && wantedFamily === grantedFamily;
  }
  if (wantedPrefix && !grantedPrefix) {
    return false;
  }
  if (!wantedPrefix && grantedPrefix) {
    const wantedFamily = permissionFamilySuffix(wanted);
    const grantedFamily = permissionFamilySuffix(granted);
    return Boolean(wantedFamily) && wantedFamily === grantedFamily;
  }
  const wantedFamily = permissionFamilySuffix(wanted);
  const grantedFamily = permissionFamilySuffix(granted);
  return Boolean(wantedFamily) && wantedFamily === grantedFamily;
}

export function permissionListMatches(permissions, wantedKey) {
  if (!Array.isArray(permissions) || !permissions.length || !wantedKey?.trim()) {
    return false;
  }
  return permissions.some((granted) => permissionKeysMatch(wantedKey, granted));
}
